import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { StaffService } from 'src/app/services/staff/staff.service';
import { SharedDataService } from '../shared.data.service';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { UserService } from 'src/app/services/core/user.service';
import {
  UserStatusService,
  StatusInfo,
} from '../header/statistics-dropdown/user-status.service';
import { finalize, Subscription } from 'rxjs';

@Component({
    selector: 'app-watcher',
    templateUrl: './watcher.component.html',
    styleUrls: ['./watcher.component.scss'],
    standalone: false
})
export class WatcherComponent implements OnInit {
  @Input() moduleID: any;
  @Input() referenceID: any;
  @Input() watchUserDetail: any;
  @Output() watchOptionSelected = new EventEmitter<{
    option: string;
    userId: number;
  }>();
  loginUserID: any;
  currentWatcher: any;
  selectedOption: string = '';
  showAddWatcherField: boolean = false;
  staffList: any = [];
  watchOptions: { name: string; icon: SafeHtml }[] = [];
  watcherSelections: Map<number, string> = new Map();
  @ViewChild('childMenuTrigger') childMenuTrigger!: MatMenuTrigger;
  @ViewChild('staffSearchMenuTrigger') staffSearchMenuTrigger!: MatMenuTrigger;
  isCurrentUserWatching: boolean = false;
  isAddingWatcher: boolean = false;
  isStaffSearchLoading: boolean = false;

  // status Indicator work
  currentStatus: any = null;
  public statusSubscription: Subscription | null = null;
  currentStatusInfo: StatusInfo | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    public backendApiService: BackendApiService,
    public staffService: StaffService,
    public sharedDataService: SharedDataService,
    public toasterService: ToasterService,
    private currentUser: UserService,
    private userStatusService: UserStatusService
  ) {}

  ngOnInit(): void {
    this.loginUserID = this.currentUser.getID();
    this.getUserCurrentStatus();
    this.renderSvgs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['watchUserDetail'] && this.watchUserDetail) {
      console.log('watcherUserDetail', this.watchUserDetail);
      // Clear existing selections first if watchUserDetail has changed completely
      if (
        changes['watchUserDetail'].currentValue !==
        changes['watchUserDetail'].previousValue
      ) {
        this.watcherSelections.clear();
      }

      this.watchUserDetail.forEach((watcher: any) => {
        if (watcher.type === 'focus_watch') {
          this.watcherSelections.set(watcher.user_id, 'Focus Watch');
        } else if (watcher.type === 'watch') {
          this.watcherSelections.set(watcher.user_id, 'Regular Watch');
        } else if (watcher.type === 'unwatch') {
          this.watcherSelections.set(watcher.user_id, 'Stop Watch');
        }
      });
      this.updateWatchingStatus();
    }
  }

  getUserCurrentStatus() {
    this.currentStatus = this.currentUser.getUserStatus();
    this.statusSubscription = this.userStatusService.status$.subscribe(
      (statusInfo) => {
        if (statusInfo) {
          this.currentStatusInfo = statusInfo;
          this.currentStatus = this.currentStatusInfo?.slug;
        }
      }
    );
  }

  updateWatchingStatus(): void {
    const watchType = this.watcherSelections.get(Number(this.loginUserID));
    this.isCurrentUserWatching =
      watchType === 'Regular Watch' || watchType === 'Focus Watch';
  }

  isUserWatching(): boolean {
    const watchType = this.watcherSelections.get(Number(this.loginUserID));
    return watchType === 'Regular Watch' || watchType === 'Focus Watch';
  }

  getUsersList(event: any) {
    const searchTerm = event?.target?.value?.trim();
    if (!searchTerm) {
      this.staffList = [];
      return;
    }

    if (this.isStaffSearchLoading) {
      return;
    }
    this.isStaffSearchLoading = true;
    this.backendApiService
      .getApi(`staff/search?search=${searchTerm}`)
      .pipe(
        finalize(() => {
          this.isStaffSearchLoading = false;
        })
      )
      .subscribe(
        (response: any) => {
          this.staffList = this.staffService.processStaff(response.data);
          if (this.staffList.length > 0) {
            setTimeout(() => {
              this.staffSearchMenuTrigger.openMenu();
            });
          }
        },
        () => {
          this.staffList = [];
        }
      );
  }

  onStaffSelected(staff: any): void {
    if (this.isAddingWatcher) {
      return;
    }
    this.staffSearchMenuTrigger.closeMenu();
    this.showAddWatcherField = false;
    this.createWatcher(staff.id);
  }

  createWatcher(userID: number): void {
    if (this.isAddingWatcher) {
      return;
    }
    this.isAddingWatcher = true;
    const payload = {
      module_id: this.moduleID,
      reference_id: this.referenceID,
      user_id: userID,
      action: 'watch',
    };

    this.sharedDataService
      .CreateWatcher(payload)
      .pipe(
        finalize(() => {
          this.isAddingWatcher = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.toasterService.showSuccessMessage(response?.message);
          this.watchOptionSelected.emit({
            option: 'Regular Watch',
            userId: userID,
          });
          this.watcherSelections.set(userID, 'Regular Watch');
          if (userID === Number(this.loginUserID)) {
            this.updateWatchingStatus();
          }
        },
        error: () => {
          this.toasterService.showErrorMessage('Failed to add watcher');
        },
      });
  }

  addLoginWatcher() {
    const isCurrentlyWatching = this.isUserWatching();

    const payload = {
      module_id: this.moduleID,
      reference_id: this.referenceID,
      user_id: this.loginUserID,
      action: isCurrentlyWatching ? 'unwatch' : 'watch',
    };

    this.sharedDataService.CreateWatcher(payload).subscribe({
      next: (response: any) => {
        this.toasterService.showSuccessMessage(response?.message);
        const newStatus = isCurrentlyWatching ? 'Stop Watch' : 'Regular Watch';
        this.watcherSelections.set(Number(this.loginUserID), newStatus);
        this.updateWatchingStatus();

        this.watchOptionSelected.emit({
          option: newStatus,
          userId: Number(this.loginUserID),
        });
      },
    });
  }

  selectOption(option: string, userId: number, event: Event): void {
    event.stopPropagation();
    this.watcherSelections.set(userId, option);
    this.childMenuTrigger.closeMenu();
    this.updateWatcherType(userId, option);
    if (userId === Number(this.loginUserID)) {
      this.updateWatchingStatus();
    }

    this.watchOptionSelected.emit({ option, userId });
  }

  toggleAddWatcherField(): void {
    this.showAddWatcherField = true;
  }

  resetAddWatcherField(): void {
    this.showAddWatcherField = false;
    this.staffList = [];
  }

  updateWatcherType(userId: number, watchType: string): void {
    let action = 'focus_watch';
    if (watchType === 'Regular Watch') {
      action = 'watch';
    } else if (watchType === 'Stop Watch') {
      action = 'unwatch';
    }

    const payload = {
      module_id: this.moduleID,
      reference_id: this.referenceID,
      user_id: userId,
      action: action,
    };

    this.sharedDataService.CreateWatcher(payload).subscribe({
      next: (response: any) => {
        this.toasterService.showSuccessMessage(response?.message);
      },
    });
  }

  getSelectedOptionIcon(userId: number): SafeHtml {
    const selectedOption = this.watcherSelections.get(userId);
    const defaultOption = 'Regular Watch';

    const option = this.watchOptions.find(
      (option) => option.name === (selectedOption || defaultOption)
    );

    return option ? option.icon : this.watchOptions[1].icon;
  }

  ngOnDestroy(): void {
    if (this.statusSubscription) {
      this.statusSubscription?.unsubscribe();
    } 
  }

  renderSvgs(): void {
    const rawWatchOptions = [
      {
        name: 'Stop Watch',
        icon: `<svg viewBox="0 0 24 24" width="25" height="25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g><path fill-rule="evenodd" clip-rule="evenodd" d="M11.9944 15.5C13.9274 15.5 15.4944 13.933 15.4944 12C15.4944 10.067 13.9274 8.5 11.9944 8.5C10.0614 8.5 8.49439 10.067 8.49439 12C8.49439 13.933 10.0614 15.5 11.9944 15.5ZM11.9944 13.4944C11.1691 13.4944 10.5 12.8253 10.5 12C10.5 11.1747 11.1691 10.5056 11.9944 10.5056C12.8197 10.5056 13.4888 11.1747 13.4888 12C13.4888 12.8253 12.8197 13.4944 11.9944 13.4944Z" fill="#ff6600"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 5C7.18879 5 3.9167 7.60905 2.1893 9.47978C0.857392 10.9222 0.857393 13.0778 2.1893 14.5202C3.9167 16.391 7.18879 19 12 19C16.8112 19 20.0833 16.391 21.8107 14.5202C23.1426 13.0778 23.1426 10.9222 21.8107 9.47978C20.0833 7.60905 16.8112 5 12 5ZM3.65868 10.8366C5.18832 9.18002 7.9669 7 12 7C16.0331 7 18.8117 9.18002 20.3413 10.8366C20.9657 11.5128 20.9657 12.4872 20.3413 13.1634C18.8117 14.82 16.0331 17 12 17C7.9669 17 5.18832 14.82 3.65868 13.1634C3.03426 12.4872 3.03426 11.5128 3.65868 10.8366Z" fill="#ff6600"/>
          <line x1="4" y1="20" x2="20" y2="4" stroke="#ff6600" stroke-width="2"/>
          </g></svg>`,
      },
      {
        name: 'Regular Watch',
        icon: `<svg viewBox="0 0 24 24" width="25" height="25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g><path fill-rule="evenodd" clip-rule="evenodd" d="M11.9944 15.5C13.9274 15.5 15.4944 13.933 15.4944 12C15.4944 10.067 13.9274 8.5 11.9944 8.5C10.0614 8.5 8.49439 10.067 8.49439 12C8.49439 13.933 10.0614 15.5 11.9944 15.5ZM11.9944 13.4944C11.1691 13.4944 10.5 12.8253 10.5 12C10.5 11.1747 11.1691 10.5056 11.9944 10.5056C12.8197 10.5056 13.4888 11.1747 13.4888 12C13.4888 12.8253 12.8197 13.4944 11.9944 13.4944Z" fill="#ff6600"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 5C7.18879 5 3.9167 7.60905 2.1893 9.47978C0.857392 10.9222 0.857393 13.0778 2.1893 14.5202C3.9167 16.391 7.18879 19 12 19C16.8112 19 20.0833 16.391 21.8107 14.5202C23.1426 13.0778 23.1426 10.9222 21.8107 9.47978C20.0833 7.60905 16.8112 5 12 5ZM3.65868 10.8366C5.18832 9.18002 7.9669 7 12 7C16.0331 7 18.8117 9.18002 20.3413 10.8366C20.9657 11.5128 20.9657 12.4872 20.3413 13.1634C18.8117 14.82 16.0331 17 12 17C7.9669 17 5.18832 14.82 3.65868 13.1634C3.03426 12.4872 3.03426 11.5128 3.65868 10.8366Z" fill="#ff6600"/>
          </g></svg>`,
      },
      {
        name: 'Focus Watch',
        icon: `<svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
        <g id="SVGRepo_iconCarrier">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M11.9944 15.5C13.9274 15.5 15.4944 13.933 15.4944 12C15.4944 10.067 13.9274 8.5 11.9944 8.5C10.0614 8.5 8.49439 10.067 8.49439 12C8.49439 13.933 10.0614 15.5 11.9944 15.5ZM11.9944 13.4944C11.1691 13.4944 10.5 12.8253 10.5 12C10.5 11.1747 11.1691 10.5056 11.9944 10.5056C12.8197 10.5056 13.4888 11.1747 13.4888 12C13.4888 12.8253 12.8197 13.4944 11.9944 13.4944Z" fill="#ff6600"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 5C7.18879 5 3.9167 7.60905 2.1893 9.47978C0.857392 10.9222 0.857393 13.0778 2.1893 14.5202C3.9167 16.391 7.18879 19 12 19C16.8112 19 20.0833 16.391 21.8107 14.5202C23.1426 13.0778 23.1426 10.9222 21.8107 9.47978C20.0833 7.60905 16.8112 5 12 5ZM3.65868 10.8366C5.18832 9.18002 7.9669 7 12 7C16.0331 7 18.8117 9.18002 20.3413 10.8366C20.9657 11.5128 20.9657 12.4872 20.3413 13.1634C18.8117 14.82 16.0331 17 12 17C7.9669 17 5.18832 14.82 3.65868 13.1634C3.03426 12.4872 3.03426 11.5128 3.65868 10.8366Z" fill="#ff6600"/>
  
          <!-- Longer Eyelashes -->
          <line x1="6.5" y1="5" x2="5" y2="2.5" stroke="#ff6600" stroke-width="1"/>
          <line x1="8" y1="4.5" x2="6.5" y2="2" stroke="#ff6600" stroke-width="1"/>
          <line x1="9.5" y1="4.2" x2="8.8" y2="1.5" stroke="#ff6600" stroke-width="1"/>
          <line x1="11" y1="4" x2="10.3" y2="1.3" stroke="#ff6600" stroke-width="1"/>
          <line x1="12.5" y1="3.9" x2="12.5" y2="1.2" stroke="#ff6600" stroke-width="1"/>
          <line x1="14" y1="4" x2="14.7" y2="1.3" stroke="#ff6600" stroke-width="1"/>
          <line x1="15.5" y1="4.2" x2="16.2" y2="1.5" stroke="#ff6600" stroke-width="1"/>
          <line x1="17" y1="4.5" x2="18.5" y2="2" stroke="#ff6600" stroke-width="1"/>
          <line x1="18.5" y1="5" x2="20" y2="2.5" stroke="#ff6600" stroke-width="1"/>
        </g>
        </svg>`,
      },
    ];

    this.watchOptions = rawWatchOptions.map((option) => ({
      ...option,
      icon: this.sanitizer.bypassSecurityTrustHtml(option.icon),
    }));
  }
}
